import {
  useGetProgramDraftWorkoutsQuery,
  useGetProgramArchivedWorkoutsQuery,
  useGetProgramScheduledWorkoutsQuery,
  useGetProgramByIdQuery,
} from '../../../../../generated/graphql';
import { resultsToAsync } from '../../../helpers/gql';
import {
  DayName,
  FixedTitle,
  LiteWorkout,
  ProgramWithWorkouts,
  RawWorkout,
  WeekName,
  WorkoutType,
  WorkoutsStructure,
} from './workouts.domain';
import { Async } from '../../../helpers/types';
import { Program } from '@playbook-technologies/common-enums';
import { useState } from 'react';
import { makeAsync } from '../../../helpers/async';
import auth from '@client/components/auth';

const toDayName = (v: string) => {
  const match = v.match(/^[Ww]\s*\d+\s*\/\s*[dD]\s*(\d+)/);
  if (!match) return { orgDay: undefined, day: undefined };
  const rawDay = parseInt(match[1], 10);
  const day = rawDay <= 0 || rawDay > 7 ? 1 : rawDay;
  return { orgDay: `D${rawDay}` as DayName, day: `D${day}` as DayName };
};

const toWeekName = (v: string) => {
  const match = v.match(/^[Ww]\s*(\d+)/);
  if (!match) return { orgWeek: undefined, week: undefined };
  const rawWeek = parseInt(match[1], 10);
  const week = rawWeek <= 0 || rawWeek > 20 ? 1 : rawWeek;
  return { orgWeek: `W${rawWeek}` as WeekName, week: `W${week}` as WeekName };
};

const toFixedTitle = (v: string) => {
  const match = v.match(/^[Ww]\s*\d+\s*\/\s*[Dd]\s*\d+\s*(.*)/);
  return (match ? match[1] : v) as FixedTitle;
};

export const toTitle = (
  w: LiteWorkout,
  delta: Pick<LiteWorkout, 'day' | 'week'>,
) => {
  const { day = w.orgDay, week = w.orgWeek } = delta;
  return `${week}/${day} ${w.fixedTitle}`;
};

export const toLiteWorkout = (
  v: RawWorkout,
  type: WorkoutType,
): LiteWorkout => {
  const title = v.name || '';

  return {
    workoutId: v.id,
    fixedTitle: toFixedTitle(title),
    orgTitle: title,
    type,
    scheduledDate: type === 'scheduled' ? v.publishedAt || '' : '',
    programId: v.programId || null,
    programIndex: v.programIndex ?? null,
    publishedDate: v.publishedAt || '',
    archivedDate: v.archivedAt,
    lastEditedDate: v.updatedAt,
    freeSample: !v.isProtected,
    rating: v.statsRatingAverage || 0,
    begins: v.statsBegins || 0,
    completions: v.statsCompletions || 0,
    comments: v.statsComments || 0,
    ...toDayName(title),
    ...toWeekName(title),
  };
};

const sortByArchivedDate = (a: LiteWorkout, b: LiteWorkout) => {
  if (!a.archivedDate || !b.archivedDate) return 0;
  return Date.parse(b.archivedDate) - Date.parse(a.archivedDate);
};

const toProgramOrderKey = (w: LiteWorkout) => {
  // Mirrors OrderedProgram.workoutsOrder: day is in 1-7 range, week 0 = no
  // week/day prefix in the title (intro/overview workouts sort first).
  const week = w.orgWeek ? parseInt(w.orgWeek.slice(1), 10) : 0;
  const day = w.orgDay ? parseInt(w.orgDay.slice(1), 10) : 0;
  return week * 10 + day;
};

const byProgramOrder = (a: LiteWorkout, b: LiteWorkout) => {
  if (
    a.programIndex !== null &&
    b.programIndex !== null &&
    a.programIndex !== b.programIndex
  ) {
    return a.programIndex - b.programIndex;
  }

  const orderDiff = toProgramOrderKey(a) - toProgramOrderKey(b);
  return orderDiff !== 0 ? orderDiff : a.workoutId - b.workoutId;
};

// The schedule modal historically saved publishedAt with wall-clock seconds,
// so batch-scheduled workouts differ at second precision while the UI only
// shows minutes. Compare at the displayed granularity and let program order
// decide within a minute.
const toScheduledMinute = (date: string) =>
  Math.floor(Date.parse(date) / 60_000);

export const sortByScheduledDate = (a: LiteWorkout, b: LiteWorkout) => {
  if (!a.scheduledDate && !b.scheduledDate) return byProgramOrder(a, b);
  if (!a.scheduledDate) return 1;
  if (!b.scheduledDate) return -1;

  const diff =
    toScheduledMinute(a.scheduledDate) - toScheduledMinute(b.scheduledDate);
  return diff !== 0 ? diff : byProgramOrder(a, b);
};

export const useProgramWithWorkouts = ({
  creatorId,
  programId,
  selectedList,
}: {
  creatorId: number;
  programId: number;
  selectedList: WorkoutType;
}): Async<ProgramWithWorkouts> => {
  const baseSkip = creatorId === null;

  const programParams = {
    variables: { id: programId },
    skip: baseSkip,
    fetchPolicy: 'network-only' as const,
  };

  const programQuery = useGetProgramByIdQuery(programParams);

  const bucketParams = (type: WorkoutType) => ({
    variables: { programId },
    skip: baseSkip || selectedList !== type,
    fetchPolicy: 'cache-and-network' as const,
  });

  const draftsQuery = useGetProgramDraftWorkoutsQuery(bucketParams('draft'));
  const archivedQuery = useGetProgramArchivedWorkoutsQuery(
    bucketParams('archived'),
  );
  const scheduledQuery = useGetProgramScheduledWorkoutsQuery(
    bucketParams('scheduled'),
  );
  const [refetching, setRefetching] = useState<Async<null>>(
    makeAsync.notFetched,
  );

  const activeBucketQuery =
    selectedList === 'draft'
      ? draftsQuery
      : selectedList === 'archived'
      ? archivedQuery
      : selectedList === 'scheduled'
      ? scheduledQuery
      : null;

  const refetch = () => {
    setRefetching(makeAsync.fetching());

    return Promise.all([
      programQuery.refetch(programParams.variables),
      activeBucketQuery ? activeBucketQuery.refetch({ programId }) : null,
    ])
      .then(() => setRefetching(makeAsync.fetched(null)))
      .catch((err) => setRefetching(makeAsync.error(err)));
  };

  const mapped = resultsToAsync([programQuery]);

  if (mapped.type !== 'fetched') return mapped;

  // setRefetching(makeAsync.fetched({}));
  const workoutsStructure: WorkoutsStructure =
    programQuery.data?.program.contentStructure ===
    Program.ContentStructure.Dayweek.value
      ? 'week/day'
      : 'series';

  const remap = (type: WorkoutType, ws: (RawWorkout | null)[] | null = []) =>
    (ws || [])
      .filter((w) => w !== null)
      .map((v) => toLiteWorkout(v as RawWorkout, type));

  const workouts: ProgramWithWorkouts['workouts'] = {
    archived: remap(
      'archived',
      archivedQuery.data?.program?.archivedWorkouts,
    ).sort(sortByArchivedDate),
    draft: remap('draft', draftsQuery.data?.program?.draftWorkouts),
    scheduled: remap(
      'scheduled',
      scheduledQuery.data?.program?.scheduledWorkouts || [],
    ).sort(sortByScheduledDate),
    published: remap(
      'published',
      programQuery.data?.program.workouts?.filter((w) => w?.isVisible),
    ),
  };

  const isAdminOrModerator = auth.isAdministrator() || auth.isModerator();
  const isProgramEditable =
    isAdminOrModerator || !programQuery.data?.program.isLocked;

  return {
    type: 'fetched' as const,
    value: {
      workouts,
      workoutsStructure,
      isProgramEditable,
      programName: programQuery.data?.program.name ?? '',
      refetchingState: refetching.type,
      isWorkoutArchiveEnabled:
        programQuery.data?.program.accessType === 'premium',
      isSelectedListLoading: activeBucketQuery?.loading ?? false,
      refetch,
    },
  };
};
